/**
 * MachineSchematic — AWG P&I Digital Twin
 * Redesign v2 — proper piping & instrumentation diagram for AWG machines
 */
import React, { useMemo } from 'react';

// ── Helpers ───────────────────────────────────────────────────────────────────
const norm = v => String(v || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const find = (arr = [], ...names) => {
  for (const name of names) {
    const needle = norm(name);
    const entry = arr.find(ch => {
      const n = norm(ch.name);
      return n === needle || n.includes(needle);
    });
    if (entry !== undefined) return entry?.value ?? entry?.state ?? null;
  }
  return null;
};

const isOn = val => {
  if (val == null) return false;
  if (typeof val === 'boolean') return val;
  if (typeof val === 'number') return val > 0;
  const s = String(val).toLowerCase();
  return s === '1' || s === 'true' || s === 'on' || s === 'yes' || s === 'running';
};

const fmtNum = (val, unit = '') => {
  if (val == null || val === '') return '--';
  const n = parseFloat(val);
  if (Number.isNaN(n)) return String(val);
  return `${n % 1 === 0 ? n.toLocaleString() : n.toFixed(1)}${unit}`;
};

const fmtPct = val => {
  const n = parseFloat(val);
  return Number.isNaN(n) ? 0 : Math.max(0, Math.min(100, n));
};

// ── Design tokens (hardcoded for SVG) ────────────────────────────────────────
const T = {
  online:       '#00875A',
  onlineBg:     '#E6F5F0',
  onlineBorder: '#B3DFCF',
  offline:      '#C42B1C',
  offlineBg:    '#FDECEA',
  offlineBorder:'#F5C0BC',
  fault:        '#9B1C1C',
  faultBg:      '#FEE2E2',
  warn:         '#B45309',
  accent:       '#0897BE',
  accentSoft:   '#E6F5FA',
  ink900:       '#0C0E16',
  ink700:       '#2D3148',
  ink500:       '#6B7194',
  ink300:       '#B6BACA',
  surface:      '#FFFFFF',
  raised:       '#F6F7F9',
  border:       '#ECEDF1',
  borderStrong: '#D4D7E0',
};

// ── Sub-components ────────────────────────────────────────────────────────────
function SummaryTile({ label, value, tone }) {
  const s = {
    online:  { bg: T.onlineBg,  border: T.onlineBorder,  text: T.online  },
    offline: { bg: T.offlineBg, border: T.offlineBorder, text: T.offline },
    accent:  { bg: T.accentSoft, border: '#C0E6F3',       text: T.accent  },
    fault:   { bg: T.faultBg,   border: '#FCA5A5',        text: T.fault   },
    neutral: { bg: T.raised,    border: T.border,          text: T.ink900  },
  }[tone] || { bg: T.raised, border: T.border, text: T.ink900 };

  return (
    <div style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 7, padding: '11px 16px', flex: 1 }}>
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5, color: T.ink500, marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 17, fontWeight: 600, color: s.text }}>{value}</div>
    </div>
  );
}

function SensorRow({ label, value, railColor }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', paddingLeft: 10, paddingTop: 5, paddingBottom: 5, borderLeft: `3px solid ${railColor}` }}>
      <div style={{ flex: 1, fontFamily: 'var(--font-sans)', fontSize: 13, color: T.ink700 }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 14, fontWeight: 600, color: T.ink900 }}>{value}</div>
    </div>
  );
}

function InterlockRow({ label, ok }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '8px 12px', borderRadius: 7, marginBottom: 6,
      background: ok ? T.onlineBg : T.faultBg,
      border: `1px solid ${ok ? T.onlineBorder : '#FCA5A5'}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: ok ? T.online : T.offline, flexShrink: 0 }} />
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 13, color: T.ink700, fontWeight: 500 }}>{label}</span>
      </div>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 12, fontWeight: 600, color: ok ? T.online : T.offline }}>
        {ok ? 'Normal' : 'Tripped'}
      </span>
    </div>
  );
}

// ── Main schematic SVG — AWG P&I style ───────────────────────────────────────
function AWGPID({ compressorOn, pumpOn, tankLevel, tankLevelLabel, compressorCount = 1 }) {
  const pipeColor   = compressorOn ? T.accent : T.borderStrong;
  const waterColor  = pumpOn ? T.accent : T.borderStrong;
  const fillH       = Math.round(120 * (tankLevel / 100));
  const fillY       = 218 - fillH;

  return (
    <svg viewBox="0 0 820 340" style={{ width: '100%', display: 'block' }}>
      <defs>
        <style>{`@keyframes pid-flow { to { stroke-dashoffset: -24; } }`}</style>
        <linearGradient id="wfill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#7dd3fc" />
          <stop offset="100%" stopColor={T.accent} />
        </linearGradient>
        <marker id="arr" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill={pipeColor} />
        </marker>
        <marker id="arrW" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill={waterColor} />
        </marker>
      </defs>

      {/* Background */}
      <rect x="0" y="0" width="820" height="340" rx="12" fill={T.raised} />

      {/* ── LABELS ─────────────────────────────────────── */}
      {/* Air intake label */}
      <text x="30" y="80" fontSize="11" fontFamily="DM Sans, sans-serif" fill={T.ink500} fontWeight="600" textDecoration="uppercase">AIR INTAKE</text>
      <text x="30" y="96" fontSize="10" fontFamily="DM Mono, monospace" fill={T.ink300}>Humid ambient air</text>

      {/* ── AIR FLOW (top path): intake → filter → evaporator ── */}
      {/* Intake arrow into pre-filter */}
      <line x1="30" y1="130" x2="68" y2="130" stroke={T.ink300} strokeWidth="2" markerEnd="url(#arr)" />
      {/* Pre-filter box */}
      <rect x="70" y="116" width="52" height="28" rx="5" fill={T.surface} stroke={T.borderStrong} strokeWidth="1.5" />
      <text x="96" y="134" textAnchor="middle" fontSize="10" fontFamily="DM Sans" fill={T.ink700} fontWeight="600">FILTER</text>
      {/* Filter → Evaporator */}
      <line x1="122" y1="130" x2="180" y2="130" stroke={T.ink300} strokeWidth="2" markerEnd="url(#arr)" />

      {/* ── EVAPORATOR (centre-left) ── */}
      <rect x="182" y="106" width="100" height="82" rx="8"
        fill={compressorOn ? T.accentSoft : T.surface}
        stroke={compressorOn ? T.accent : T.borderStrong}
        strokeWidth={compressorOn ? 2 : 1.5}
      />
      {/* Coil lines inside */}
      {[0,1,2,3].map(i => (
        <line key={i} x1="194" y1={120 + i * 16} x2="270" y2={120 + i * 16} stroke={compressorOn ? T.accent : T.borderStrong} strokeWidth="2" opacity="0.5" />
      ))}
      <text x="232" y="175" textAnchor="middle" fontSize="12" fontFamily="DM Sans" fontWeight="600" fill={T.ink900}>Evaporator</text>

      {/* Condensate drop arrow → water filter */}
      <line x1="232" y1="190" x2="232" y2="218" stroke={waterColor} strokeWidth="2.5" strokeDasharray={compressorOn ? "6 4" : "4 4"} style={compressorOn ? { animation: 'pid-flow 1s linear infinite' } : {}} markerEnd="url(#arrW)" />
      <text x="242" y="210" fontSize="9" fontFamily="DM Sans" fill={T.ink500}>Condensate</text>

      {/* ── WATER FILTER (below evaporator) ── */}
      <rect x="200" y="220" width="64" height="26" rx="5" fill={T.surface} stroke={T.borderStrong} strokeWidth="1.5" />
      <text x="232" y="237" textAnchor="middle" fontSize="10" fontFamily="DM Sans" fill={T.ink700} fontWeight="600">W. FILTER</text>

      {/* Filter → UV lamp */}
      <line x1="264" y1="233" x2="300" y2="233" stroke={waterColor} strokeWidth="2.5" strokeDasharray={pumpOn ? "6 4" : "none"} style={pumpOn ? { animation: 'pid-flow 1s linear infinite' } : {}} />

      {/* ── UV LAMP ── */}
      <rect x="302" y="222" width="46" height="22" rx="5" fill="#F0F9FF" stroke="#60A5FA" strokeWidth="1.5" />
      <text x="325" y="237" textAnchor="middle" fontSize="10" fontFamily="DM Sans" fill="#1D4ED8" fontWeight="600">UV</text>

      {/* UV → tank */}
      <line x1="348" y1="233" x2="380" y2="233" stroke={waterColor} strokeWidth="2.5" strokeDasharray={pumpOn ? "6 4" : "none"} style={pumpOn ? { animation: 'pid-flow 1s linear infinite' } : {}} markerEnd="url(#arrW)" />

      {/* ── WATER TANK ── */}
      <rect x="382" y="98" width="72" height="150" rx="10"
        fill={T.surface}
        stroke={T.accent}
        strokeWidth="2.5"
      />
      {/* Fill level */}
      {fillH > 0 && (
        <rect x="385" y={fillY} width="66" height={fillH} rx="8"
          fill="url(#wfill)"
          style={{ transition: 'height 0.6s ease' }}
        />
      )}
      <text x="418" y="90" textAnchor="middle" fontSize="11" fontFamily="DM Sans" fontWeight="600" fill={T.ink500}>Water Tank</text>
      <text x="418" y="168" textAnchor="middle" fontSize="22" fontFamily="DM Mono" fontWeight="700" fill={T.ink900}>{tankLevelLabel ?? `${Math.round(tankLevel)}%`}</text>
      {/* Level marks */}
      {[25, 50, 75].map(pct => {
        const y = 98 + 150 * (1 - pct / 100);
        return <line key={pct} x1="384" y1={y} x2="454" y2={y} stroke={T.border} strokeDasharray="4 4" strokeWidth="1" />;
      })}
      {/* Outlet pipe */}
      <line x1="454" y1="220" x2="510" y2="220" stroke={waterColor} strokeWidth="2.5" strokeDasharray={pumpOn ? "6 4" : "none"} style={pumpOn ? { animation: 'pid-flow 1s linear infinite' } : {}} />

      {/* ── PUMP ── */}
      <circle cx="530" cy="220" r="22"
        fill={pumpOn ? T.onlineBg : T.surface}
        stroke={pumpOn ? T.online : T.offlineBorder}
        strokeWidth="2"
      />
      {/* pump impeller icon */}
      <path d="M522,216 Q530,208 538,216 Q530,224 522,216" fill="none" stroke={pumpOn ? T.online : T.ink300} strokeWidth="1.5" />
      <text x="530" y="218" textAnchor="middle" fontSize="9" fontFamily="DM Sans" fontWeight="700" fill={T.ink900}>PUMP</text>
      <text x="530" y="229" textAnchor="middle" fontSize="9" fontFamily="DM Sans" fontWeight="600" fill={pumpOn ? T.online : T.offline}>{pumpOn ? 'RUN' : 'STOP'}</text>
      {/* Pump → post-filter */}
      <line x1="552" y1="220" x2="584" y2="220" stroke={waterColor} strokeWidth="2.5" strokeDasharray={pumpOn ? "6 4" : "none"} style={pumpOn ? { animation: 'pid-flow 1s linear infinite' } : {}} />

      {/* ── POST-FILTER ── */}
      <rect x="586" y="208" width="54" height="24" rx="5" fill={T.surface} stroke={T.borderStrong} strokeWidth="1.5" />
      <text x="613" y="224" textAnchor="middle" fontSize="10" fontFamily="DM Sans" fill={T.ink700} fontWeight="600">POST-FILT</text>
      {/* post-filter → outlet */}
      <line x1="640" y1="220" x2="690" y2="220" stroke={waterColor} strokeWidth="2.5" strokeDasharray={pumpOn ? "6 4" : "none"} style={pumpOn ? { animation: 'pid-flow 1s linear infinite' } : {}} markerEnd="url(#arrW)" />
      <text x="700" y="217" fontSize="11" fontFamily="DM Sans" fill={T.ink500} fontWeight="600">Outlet</text>
      <text x="700" y="230" fontSize="10" fontFamily="DM Mono" fill={pumpOn ? T.accent : T.ink300}>{pumpOn ? 'Flow' : 'Standby'}</text>

      {/* ── REFRIGERATION CYCLE ── */}
      {/* COMPRESSOR (bottom-left) */}
      <rect x="60" y="230" width="110" height="70" rx="10"
        fill={compressorOn ? T.onlineBg : T.surface}
        stroke={compressorOn ? T.online : T.borderStrong}
        strokeWidth={compressorOn ? 2.5 : 1.5}
      />
      <text x="115" y="260" textAnchor="middle" fontSize="13" fontFamily="DM Sans" fontWeight="700" fill={T.ink900}>Compressor</text>
      <text x="115" y="278" textAnchor="middle" fontSize="11" fontFamily="DM Sans" fontWeight="600" fill={compressorOn ? T.online : T.ink300}>
        {compressorOn ? 'ONLINE' : 'OFFLINE'}
      </text>
      {/* Compressor → Condenser (refrigerant hot gas line) */}
      <path d="M170,255 Q260,255 300,190 Q330,145 360,145" stroke={compressorOn ? T.accent : T.border} strokeWidth="2.5" strokeDasharray={compressorOn ? "7 4" : "5 5"} fill="none" style={compressorOn ? { animation: 'pid-flow 1.4s linear infinite' } : {}} />

      {/* ── CONDENSER (top-right of cycle) ── */}
      <rect x="560" y="80" width="100" height="80" rx="8"
        fill={T.surface} stroke={T.borderStrong} strokeWidth="1.5"
      />
      {[0,1,2,3].map(i => (
        <line key={i} x1="572" y1={96 + i * 14} x2="648" y2={96 + i * 14} stroke={T.border} strokeWidth="1.5" />
      ))}
      <text x="610" y="146" textAnchor="middle" fontSize="12" fontFamily="DM Sans" fontWeight="600" fill={T.ink900}>Condenser</text>
      <text x="610" y="75" textAnchor="middle" fontSize="10" fontFamily="DM Sans" fill={T.ink500}>Fan cooled</text>

      {/* Condenser → Expansion valve → Evaporator (cold line) */}
      <path d="M560,130 Q460,130 420,130 Q320,130 282,130" stroke={compressorOn ? '#60A5FA' : T.border} strokeWidth="2" strokeDasharray={compressorOn ? "6 4" : "5 5"} fill="none" style={compressorOn ? { animation: 'pid-flow 1.4s linear infinite' } : {}} />
      {/* Expansion valve symbol */}
      <polygon points="446,122 460,130 446,138" fill={compressorOn ? '#60A5FA' : T.border} />
      <polygon points="460,122 446,130 460,138" fill={compressorOn ? '#60A5FA' : T.border} />
      <text x="453" y="148" textAnchor="middle" fontSize="9" fontFamily="DM Sans" fill={T.ink500}>EXP</text>

      {/* Evaporator → Compressor (suction line) */}
      <path d="M182,170 Q140,170 115,230" stroke={compressorOn ? T.accent : T.border} strokeWidth="2" strokeDasharray={compressorOn ? "6 4" : "5 5"} fill="none" style={compressorOn ? { animation: 'pid-flow 1.4s linear infinite' } : {}} />

      {/* ── HOT GAS label ── */}
      <text x="270" y="250" fontSize="9" fontFamily="DM Sans" fill={T.ink300} fontStyle="italic">Hot gas</text>
      <text x="340" y="165" fontSize="9" fontFamily="DM Sans" fill="#93C5FD" fontStyle="italic">Cold liquid</text>
      <text x="130" y="202" fontSize="9" fontFamily="DM Sans" fill={T.ink300} fontStyle="italic">Suction</text>
    </svg>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
const MachineSchematic = ({ statusData = {} }) => {
  const di     = useMemo(() => statusData.digital_input  || [], [statusData]);
  const ai     = useMemo(() => statusData.analog_input   || [], [statusData]);
  const ao     = useMemo(() => statusData.analog_output  || [], [statusData]);
  const others = useMemo(() => statusData.others         || [], [statusData]);
  const all    = useMemo(() => [...ai, ...ao, ...others], [ai, ao, others]);

  // ANY compressor on = machine running (handles Compressor 1–4, LP1–4, HP1–3 naming)
  const compressorOn = useMemo(() => di.some(ch => norm(ch.name).includes('compressor') && isOn(ch.value)), [di]);
  // No dedicated pump channel — derive from compressor running
  const pumpOn       = useMemo(() => {
    const explicit = di.find(ch => norm(ch.name).includes('pump'));
    return explicit ? isOn(explicit.value) : compressorOn;
  }, [di, compressorOn]);
  // LP/HP fault = ANY LP or HP channel is tripped (ON)
  const lpOk  = useMemo(() => !di.some(ch => norm(ch.name).startsWith('lp') && isOn(ch.value)), [di]);
  const hpOk  = useMemo(() => !di.some(ch => norm(ch.name).startsWith('hp') && isOn(ch.value)), [di]);
  const olrOk = useMemo(() => !isOn(find(di, 'olr', 'overload', 'overloadrelay')), [di]);

  const temperature  = useMemo(() => find(all, 'temperature', 'temp'), [all]);
  const humidity     = useMemo(() => find(all, 'humidity', 'hum'), [all]);
  const dewPoint     = useMemo(() => find(all, 'dewpoint', 'dew point', 'dew'), [all]);

  // Water level may be text ("high"/"medium"/"low") or numeric — map both
  const tankLevelRaw = useMemo(() => find(all, 'tanklevel', 'waterlevel', 'water level', 'tank level'), [all]);
  const tankLevel    = useMemo(() => {
    if (tankLevelRaw == null) return 0;
    const s = String(tankLevelRaw).toLowerCase().trim();
    if (s === 'high')   return 90;
    if (s === 'medium') return 55;
    if (s === 'low')    return 20;
    return fmtPct(tankLevelRaw);
  }, [tankLevelRaw]);
  const tankLevelLabel = useMemo(() => {
    if (tankLevelRaw == null) return '--';
    const s = String(tankLevelRaw).toLowerCase().trim();
    if (['high','medium','low'].includes(s)) return s.charAt(0).toUpperCase() + s.slice(1);
    return `${Math.round(fmtPct(tankLevelRaw))}%`;
  }, [tankLevelRaw]);

  const dailyYield = useMemo(() => find(all,
    'daily water', 'dailywater', 'daily water generated', 'dailywatergenerated',
    'daily water(l)', 'dailywater(l)', 'yield', 'dailyyield'
  ), [all]);
  const runtime = useMemo(() => find(all, 'runtime', 'run time', 'run time(hh:mm:ss)'), [all]);

  const running  = compressorOn;
  const runState = compressorOn ? 'RUNNING' : 'IDLE';

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(12,14,22,0.07)' }}>

      {/* Header */}
      <div style={{ padding: '15px 20px', borderBottom: `1px solid ${T.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 3 }}>
          <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 15, color: T.ink900 }}>Digital Twin</span>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5,
            padding: '3px 9px', borderRadius: 4,
            background: running ? T.onlineBg : T.raised,
            color: running ? T.online : T.ink500,
            border: `1px solid ${running ? T.onlineBorder : T.border}`,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: running ? T.online : T.ink300, animation: running ? 'atoms-pulse 2s infinite' : 'none' }} />
            {runState}
          </span>
        </div>
        <div style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: T.ink500 }}>
          AWG live P&amp;I — refrigeration cycle, water path, interlocks
        </div>
      </div>

      {/* Summary tiles */}
      <div style={{ display: 'flex', gap: 10, padding: '14px 20px', borderBottom: `1px solid ${T.border}` }}>
        <SummaryTile label="Compressor"  value={compressorOn ? 'Online' : 'Offline'} tone={compressorOn ? 'online' : 'offline'} />
        <SummaryTile label="Pump"        value={pumpOn ? 'Running' : 'Stopped'}       tone={pumpOn ? 'online' : 'offline'} />
        <SummaryTile label="Tank Level"  value={tankLevelLabel}                         tone="accent" />
        <SummaryTile label="Daily Yield" value={dailyYield != null ? `${fmtNum(dailyYield)} L` : '--'} tone="neutral" />
      </div>

      {/* Body */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.7fr) 280px' }}>

        {/* P&I Diagram */}
        <div style={{ padding: 16, background: T.raised, borderRight: `1px solid ${T.border}` }}>
          <AWGPID compressorOn={compressorOn} pumpOn={pumpOn} tankLevel={tankLevel} tankLevelLabel={tankLevelLabel} />
        </div>

        {/* Right panel */}
        <div style={{ padding: '18px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Environmental sensors */}
          <div>
            <div style={{ fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.7, color: T.ink500, marginBottom: 10 }}>
              Environmental Sensors
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <SensorRow label="Temperature" value={`${fmtNum(temperature)} °C`} railColor={T.accent} />
              <SensorRow label="Humidity"    value={`${fmtNum(humidity)} %`}      railColor={T.online} />
              <SensorRow label="Dew Point"   value={`${fmtNum(dewPoint)} °C`}     railColor={T.warn} />
            </div>
          </div>

          <div style={{ height: 1, background: T.border }} />

          {/* Protection & Runtime */}
          <div>
            <div style={{ fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.7, color: T.ink500, marginBottom: 10 }}>
              Protection &amp; Runtime
            </div>
            <InterlockRow label="Low Pressure"   ok={lpOk}  />
            <InterlockRow label="High Pressure"  ok={hpOk}  />
            <InterlockRow label="Overload Relay" ok={olrOk} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 12px', borderRadius: 7, border: `1px solid ${T.border}`, background: T.raised, marginTop: 2 }}>
              <span style={{ fontFamily: 'var(--font-sans)', fontSize: 13, color: T.ink700, fontWeight: 500 }}>Run Time</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 600, color: T.ink900 }}>{runtime || '--'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MachineSchematic;
